import { useState, useMemo, useLayoutEffect } from "react";
import Utils, { _ } from "~/common/Utils";
import BindingManager from "./BindingManager";
import ConfigManager from "~/common/caching/ConfigManager.js";
import LocalizationManager from "~/common/caching/LocalizationManager.js";

const ErrorBoundary = Utils.component("common/ErrorBoundary");

const App = Utils.createLazyComponent(async () => {
    const { BrowserRouter } = await import("react-router-dom");
    const { useOnTheMoveContext } = await import("~/common/hooks/OnTheMoveContext");
    const { RecordProvider } = await import("~/common/hooks/RecordContext");
    const { fallback } = await import("~/components/common/ErrorBoundary");
    const { default: Router } = await import("~/common/Router");

    const data = await ConfigManager.build();

    await LocalizationManager.tryInitialize(_.Settings.Setting('Application.SelectedLanguage'));

    return () => {
        const { contextData, setContextData } = useOnTheMoveContext();
        const [isLoading, setIsLoading] = useState(true);

        useLayoutEffect(() => {
            setContextData(d => ({ ...d, ...data }));
        }, [setContextData]);

        const globalComponents = useMemo(() => {
            if (Object.keys(contextData).length > 0) {
                const { func: globalFuncs, bc: globalBcs, ...result } = contextData.defaultSettings.globals;

                let promiseRun = _.Async.Combine({
                    promises: globalBcs?.map((bc) => _.BC.Query({ bc })) || []
                });

                globalFuncs?.forEach(funcName => {
                    const nextFunc = BindingManager.issueBinding(funcName, window);

                    promiseRun = _.Async.Chain(promiseRun, () => typeof nextFunc === 'function' ? nextFunc() : _.Async.WrapInPromise(nextFunc));
                });

                promiseRun.Always(() => {
                    setIsLoading(false);
                });

                return result;
            }
            else {
                return null;
            }
        }, [contextData]);

        return isLoading ? fallback() : (
            <div className="App main-section">
                <BrowserRouter>
                    <RecordProvider>
                        <ErrorBoundary css={{ container: "main-section" }}>
                            {Utils.loadGlobalComponents(globalComponents)}
                            <Router globalComponents={globalComponents} />
                        </ErrorBoundary>
                    </RecordProvider>
                </BrowserRouter>
            </div>
        );
    };
});

export default App;
